import React from "react";
import {
  Container, Title, Text, Box, Group, Button,
  SimpleGrid, Card, ThemeIcon, List, Badge,
  Divider, Stack, rem,
} from "@mantine/core";
import {
  IconCalendarEvent, IconBrandWhatsapp, IconCreditCard,
  IconUsers, IconChartBar, IconCheck, IconArrowLeft,
  IconStar, IconShieldCheck, IconClock, IconDeviceMobile,
} from "@tabler/icons";
import Link from "next/link";
import METATAGSEO from "@/components/metaTagSEO";
import JsonLd from "@/components/JsonLd";
import { siteConfig } from "@/components/seoConfig";

const seoData = {
  title: "מה זה QuickTor? | מדריך מלא למערכת ניהול תורים ישראלית",
  description:
    "QuickTor היא מערכת ניהול תורים ישראלית ב-99₪/חודש. זימון תורים אונליין, תזכורות ווטסאפ, גביית תשלום מראש וניהול לקוחות  הכל בעברית. 14 יום ניסיון חינם.",
  canonicalUrl: `${siteConfig.baseUrl}/about-quicktor`,
  OgImageUrl: `${siteConfig.baseUrl}/sectionmainpage.webp`,
  altText: "QuickTor  מערכת ניהול תורים לעסקים בישראל",
  keywords:
    "מה זה QuickTor, מערכת ניהול תורים ישראלית, אפליקציה לניהול תורים, ניהול עסק תורים, תוכנה לניהול תורים",
  isPartOf: "https://quicktor.co.il/",
};

const BreadcrumbListJson = [
  { "@type": "ListItem", item: `${siteConfig.baseUrl}/`, name: "דף הבית", position: 1 },
  { "@type": "ListItem", item: `${siteConfig.baseUrl}/about-quicktor`, name: "מה זה QuickTor?", position: 2 },
];

const FEATURES = [
  { icon: IconCalendarEvent, title: "זימון תורים אונליין", desc: "דף עסקי ממותג עם יומן תורים פעיל 24/7. הלקוח בוחר שירות, תאריך ושעה  ומקבל אישור לווטסאפ.", color: "blue" },
  { icon: IconBrandWhatsapp, title: "תזכורות ווטסאפ", desc: "המערכת שולחת תזכורות אוטומטיות ללקוחות לפני התור. פחות שכחה = פחות ביטולים.", color: "green" },
  { icon: IconCreditCard, title: "תשלום מראש", desc: "גבו מקדמה או תשלום מלא בזמן קביעת התור. לקוח שמשלם מראש כמעט אף פעם לא מבטל.", color: "violet" },
  { icon: IconUsers, title: "ניהול עובדים", desc: "יומנים נפרדים לכל עובד, שירותים ומחירים ייחודיים, תצוגת מנהל כוללת.", color: "orange" },
  { icon: IconChartBar, title: "דוחות ביצועים", desc: "לוח בקרה עם הכנסות, מספר תורים, אחוזי הגעה ולקוחות חדשים  לפי יום, שבוע או חודש.", color: "pink" },
  { icon: IconDeviceMobile, title: "Web App למובייל", desc: "עובדת מכל דפדפן  אייפון, אנדרואיד, טאבלט ומחשב. ניתן להוסיף למסך הבית.", color: "cyan" },
];

const PROFESSIONS = [
  { label: "קוסמטיקאיות", href: "/queue-management-beauticians" },
  { label: "מספרות וספרים", href: "/managing-queues-hair-salons" },
  { label: "נייליסטיות", href: "/queue-management-nail-techs" },
  // { label: "מטפלים הוליסטיים", href: "/faq" },
  // { label: "מאמני כושר", href: "/faq" },
];

const STATS = [
  { value: "99₪", label: "לחודש בלבד" },
  { value: "14", label: "ימי ניסיון חינם" },
  { value: "וואטסאפ", label: "תזכורות שמפחיתות ביטולים" },
  { value: "24/7", label: "קביעת תורים" },
];

// HowTo Schema
const howToSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "איך מתחילים עם QuickTor",
  description: "מדריך שלב-שלב להתחלת עבודה עם מערכת ניהול תורים QuickTor",
  totalTime: "PT1H",
  step: [
    { "@type": "HowToStep", position: 1, name: "הרשמה", text: "נרשמים תוך פחות מ-5 דקות" },
    { "@type": "HowToStep", position: 2, name: "הגדרת שירותים", text: "מגדירים שירותים, מחירים ושעות פעילות" },
    { "@type": "HowToStep", position: 3, name: "שיתוף הלינק", text: "שולחים ללקוחות את הלינק לדף העסקי" },
    { "@type": "HowToStep", position: 4, name: "קבלת תורים", text: "הלקוחות מתחילים לקבוע תורים אוטומטית" },
  ],
};

export default function AboutQuickTor() {
  return (
    <Box style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      <METATAGSEO {...seoData} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }}
      />
      <JsonLd
        canonical={seoData.canonicalUrl}
        title={seoData.title}
        description={seoData.description}
        OgImageUrl={seoData.OgImageUrl}
        isPartOf="https://quicktor.co.il/"
        BreadcrumbListJson={BreadcrumbListJson}
      />

      {/* ── Hero ── */}
      <Box
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          padding: "80px 0 60px",
          borderRadius: "0 0 50px 50px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          style={{
            position: "absolute", inset: 0,
            background: "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.15) 0%, transparent 50%)",
            pointerEvents: "none",
          }}
        />
        <Container size="lg" style={{ position: "relative", zIndex: 1 }}>
          {/* Breadcrumb */}
          <Group mb="lg" gap={8}>
            <Link href="/" style={{ color: "rgba(255,255,255,0.75)", fontSize: 14, textDecoration: "none" }}>דף הבית</Link>
            <Text c="rgba(255,255,255,0.5)" size="sm">/</Text>
            <Text c="white" size="sm" fw={600}>מה זה QuickTor?</Text>
          </Group>

          <Title
            order={1}
            style={{ fontSize: "clamp(2rem, 5vw, 3.2rem)", fontWeight: 900, color: "white", lineHeight: 1.2, marginBottom: 20 }}
          >
            מה זה QuickTor?
            <br />
            <Text span inherit style={{ color: "rgba(255,255,255,0.85)", fontSize: "70%" }}>
              המדריך המלא למערכת ניהול תורים ישראלית
            </Text>
          </Title>

          <Text size="xl" c="rgba(255,255,255,0.92)" maw={650} mb="xl" style={{ lineHeight: 1.7 }}>
            QuickTor היא מערכת <strong>All-in-One</strong> לניהול תורים, לקוחות ותשלומים,
            שנבנתה מהיסוד לעסקים קטנים ובינוניים בישראל  בעברית מלאה, עם סליקת אשראי נוחה וחיבור ישיר לוואטסאפ.
          </Text>

          <Group gap="sm" wrap="wrap">
            <Button
              component="a"
              href="https://app.quicktor.co.il/MainPageToConnect/MainPage"
              size="lg" radius="xl"
              style={{ background: "white", color: "#764ba2", fontWeight: 800 }}
            >
              התחל 14 יום חינם
            </Button>
            <Button
              component="a"
              href={`https://wa.me/972557259188?text=שלום, אשמח לשמוע על QuickTor`}
              target="_blank" rel="noopener noreferrer"
              size="lg" radius="xl" variant="outline"
              leftSection={<IconBrandWhatsapp size={18} />}
              style={{ borderColor: "rgba(255,255,255,0.6)", color: "white" }}
            >
              שאל אותנו בווטסאפ
            </Button>
          </Group>
        </Container>
      </Box>

      <Container size="lg" py={60}>

        {/* ── Stats ── */}
        <SimpleGrid cols={{ base: 2, sm: 4 }} mb={80} spacing="xl">
          {STATS.map((s) => (
            <Box key={s.value} ta="center" p="xl" style={{
              background: "white", borderRadius: 20,
              boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
              border: "1px solid #e9ecef",
            }}>
              <Text fw={900} style={{ fontSize: "clamp(1.75rem, 4vw, 2.5rem)", background: "linear-gradient(135deg, #667eea, #764ba2)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                {s.value}
              </Text>
              <Text c="dimmed" size="sm" fw={500}>{s.label}</Text>
            </Box>
          ))}
        </SimpleGrid>

        {/* ── What is QuickTor ── */}
        <Box mb={80}>
          <Title order={2} mb="md" style={{ color: "#1a1a2e", fontSize: "clamp(1.5rem, 3vw, 2rem)" }}>
            📌 הגדרה מדויקת  מה זה QuickTor?
          </Title>
          <Box
            p="xl"
            style={{
              background: "white", borderRadius: 20,
              borderRight: "6px solid #667eea",
              boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
            }}
          >
            <Text size="lg" style={{ lineHeight: 1.8, color: "#333" }}>
              <strong>QuickTor</strong> היא תוכנה  ישראלית  לניהול העסק והתורים.
              היא מאפשרת לבעלי עסקים לנהל תורים, לקוחות, תשלומים ועובדים  הכל ממקום אחד.
              <br /><br />
              המערכת כוללת דף עסקי ממותג שמקדם את העסק בגוגל, שולחת תזכורות ווטסאפ אוטומטיות,
              וגובה תשלום מראש למניעת ביטולים.
              <br /><br />
              <strong>מחיר:</strong> 99₪ לחודש בלבד, עם 14 ימי ניסיון חינם .
            </Text>
          </Box>
        </Box>

        {/* ── Features ── */}
        <Box mb={80}>
          <Title order={2} mb="xs" style={{ color: "#1a1a2e", fontSize: "clamp(1.5rem, 3vw, 2rem)" }}>
            ⚡ הפיצ׳רים המרכזיים של QuickTor
          </Title>
          <Text c="dimmed" mb="xl" size="lg">כל מה שעסק מבוסס תורים צריך  במקום אחד</Text>

          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="xl">
            {FEATURES.map((f) => (
              <Card key={f.title} p="xl" radius="xl" withBorder shadow="sm"
                style={{
                  border: "1px solid #e9ecef",
                  transition: "all 0.3s ease",
                  background: "white",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 12px 30px rgba(102,126,234,0.15)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = ""; }}
              >
                <ThemeIcon size={52} radius="xl" variant="light" color={f.color} mb="md">
                  <f.icon size={26} />
                </ThemeIcon>
                <Title order={3} size="h4" mb="xs" style={{ color: "#1a1a2e" }}>{f.title}</Title>
                <Text c="dimmed" size="sm" style={{ lineHeight: 1.7 }}>{f.desc}</Text>
              </Card>
            ))}
          </SimpleGrid>
        </Box>

        {/* ── How it works ── */}
        <Box mb={80} p="xl" style={{ background: "white", borderRadius: 24, boxShadow: "0 4px 20px rgba(0,0,0,0.06)", border: "1px solid #e9ecef" }}>
          <Title order={2} mb="xl" ta="center" style={{ color: "#1a1a2e" }}>
            🚀 איך מתחילים עם QuickTor?
          </Title>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="xl">
            {[
              { n: "1", title: "נרשמים", desc: "פחות מ-5 דקות, " },
              { n: "2", title: "מגדירים", desc: "שירותים, מחירים ושעות פעילות" },
              { n: "3", title: "משתפים", desc: "שולחים ללקוחות את הלינק לדף העסקי" },
              { n: "4", title: "מרוויחים", desc: "הלקוחות קובעים תורים  אוטומטי" },
            ].map((step) => (
              <Stack key={step.n} align="center" gap="xs" ta="center">
                <Box style={{
                  width: 56, height: 56, borderRadius: "50%",
                  background: "linear-gradient(135deg, #667eea, #764ba2)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "white", fontSize: 22, fontWeight: 900,
                }}>
                  {step.n}
                </Box>
                <Text fw={700} style={{ color: "#1a1a2e" }}>{step.title}</Text>
                <Text size="sm" c="dimmed">{step.desc}</Text>
              </Stack>
            ))}
          </SimpleGrid>
        </Box>

        {/* ── For whom ── */}
        <Box mb={80}>
          <Title order={2} mb="xs" style={{ color: "#1a1a2e" }}>
            👥 למי מיועדת QuickTor?
          </Title>
          <Text c="dimmed" mb="xl" size="lg">כל עסק שמבוסס על תורים ופגישות</Text>

          <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="md" mb="xl">
            {PROFESSIONS.map((p) => (
              <Link key={p.href + p.label} href={p.href} style={{ textDecoration: "none" }}>
                <Box
                  p="md"
                  style={{
                    background: "white", borderRadius: 14,
                    border: "2px solid #e9ecef",
                    textAlign: "center", cursor: "pointer",
                    transition: "all 0.2s ease",
                    color: "#1a1a2e", fontWeight: 600,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#667eea"; e.currentTarget.style.color = "#667eea"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e9ecef"; e.currentTarget.style.color = "#1a1a2e"; }}
                >
                  {p.label}
                </Box>
              </Link>
            ))}
          </SimpleGrid>
        </Box>

        {/* ── Comparison ── */}
        <Box mb={80}>
          <Title order={2} mb="xl" style={{ color: "#1a1a2e" }}>
            🆚 QuickTor לעומת חלופות נפוצות
          </Title>
          <Box style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", background: "white", borderRadius: 16, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.06)" }}>
              <thead>
                <tr style={{ background: "linear-gradient(135deg, #667eea, #764ba2)", color: "white" }}>
                  {["פיצ׳ר", "QuickTor", "גוגל קלנדר", "יומן ידני"].map((h) => (
                    <th key={h} style={{ padding: "14px 16px", textAlign: "center", fontWeight: 700, fontSize: 14 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ["ממשק קל וידידותי", "✅", "❌", "✅"],
                  ["סליקת אשראי וחשבוניות", "✅", "❌", "❌"],
                  ["תזכורות אוטומטיות בוואטסאפ", "✅", "❌", "❌"],
                  ["קביעת תורים אונליין 24/7", "✅", "חלקי", "❌"],
                  ["גביית מקדמה מלקוחות", "✅", "❌", "❌"],
                  ["דוחות וסטטיסטיקות", "✅", "❌", "❌"],
                  ["מחיר חודשי (הכל כלול)", "99₪", "חינם", "חינם"],
                ].map((row, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #f1f3f5", background: i % 2 === 0 ? "white" : "#fafbff" }}>
                    {row.map((cell, j) => (
                      <td key={j} style={{
                        padding: "12px 16px", textAlign: "center", fontSize: 14,
                        fontWeight: j === 0 ? 600 : 400,
                        color: j === 1 ? "#667eea" : "#555",
                      }}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </Box>

        {/* ── Internal links ── */}
        <Box mb={60} p="xl" style={{ background: "white", borderRadius: 20, border: "1px solid #e9ecef" }}>
          <Title order={3} mb="md" style={{ color: "#1a1a2e" }}>📚 מידע נוסף על QuickTor</Title>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            {[
              { href: "/pricing", label: "מחירון מלא  כל מה שכלול ב-99₪" },
              { href: "/faq", label: "שאלות ותשובות נפוצות (33 שאלות)" },
              { href: "/about", label: "הסיפור שלנו  מי בנה את QuickTor?" },
              { href: "/magazine", label: "בלוג ומדריכים מקצועיים" },
              { href: "/queue-management-beauticians", label: "QuickTor לקוסמטיקאיות" },
              { href: "/managing-queues-hair-salons", label: "QuickTor למספרות" },
              { href: "/queue-management-nail-techs", label: "QuickTor לנייליסטיות" },
            ].map((l) => (
              <Link key={l.href} href={l.href}
                style={{
                  display: "block", padding: "10px 16px",
                  borderRadius: 10, color: "#667eea",
                  background: "#f8f9ff", fontWeight: 500,
                  textDecoration: "none", fontSize: 14,
                  border: "1px solid rgba(102,126,234,0.15)",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#667eea"; e.currentTarget.style.color = "white"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "#f8f9ff"; e.currentTarget.style.color = "#667eea"; }}
              >
                {l.label}
              </Link>
            ))}
          </SimpleGrid>
        </Box>

        {/* ── CTA ── */}
        <Box
          p={60} ta="center"
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: 30,
          }}
        >
          <Title order={2} c="white" mb="md" style={{ fontSize: "clamp(1.75rem, 4vw, 2.5rem)", fontWeight: 900 }}>
            מוכן לנסות את QuickTor?
          </Title>
          <Text c="rgba(255,255,255,0.9)" mb="xl" size="lg">
            14 יום חינם  , ללא התחייבות
          </Text>
          <Group justify="center" gap="md" wrap="wrap">
            <Button
              component="a"
              href="https://app.quicktor.co.il/MainPageToConnect/MainPage"
              size="xl" radius="xl"
              style={{ background: "white", color: "#764ba2", fontWeight: 800, boxShadow: "0 8px 30px rgba(0,0,0,0.15)" }}
            >
              התחל בחינם עכשיו
            </Button>
            <Button
              component={Link} href="/pricing"
              size="xl" radius="xl" variant="outline"
              style={{ borderColor: "rgba(255,255,255,0.6)", color: "white" }}
              leftSection={<IconArrowLeft size={18} />}
            >
              ראה מחירון
            </Button>
          </Group>
        </Box>
      </Container>
    </Box>
  );
}
